
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Guard } from "../types";
import { Search } from "lucide-react";

interface GuardSearchDialogProps {
  guards: Guard[];
  isLoading: boolean;
  onSelectGuard?: (guard: Guard) => void;
  buttonLabel?: string; // Add this new prop
}

export const GuardSearchDialog = ({ guards, isLoading, onSelectGuard, buttonLabel }: GuardSearchDialogProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleGuardSelect = (guard: Guard) => {
    setSearch("");
    setOpen(false);
    onSelectGuard?.(guard);
  };

  // Sort guards alphabetically by last name, then by first name
  const sortedGuards = [...guards].sort((a, b) => {
    // Sort by last name first
    const lastNameComparison = (a.last_name || '').localeCompare(b.last_name || '');
    if (lastNameComparison !== 0) return lastNameComparison;
    
    // If last names are the same, sort by first name
    return (a.first_name || '').localeCompare(b.first_name || '');
  });

  let filteredGuards = sortedGuards;
  if (search) {
    const searchLower = search.toLowerCase();
    filteredGuards = sortedGuards.filter(
      (guard) =>
        `${guard.last_name} ${guard.first_name}`.toLowerCase().includes(searchLower) ||
        guard.email?.toLowerCase().includes(searchLower) ||
        guard.code?.toLowerCase().includes(searchLower) ||
        guard.mobile_phone?.toLowerCase().includes(searchLower)
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Label
          htmlFor="search"
          className="bg-secondary hover:bg-secondary/80 transition-colors rounded-md px-4 py-2 w-full text-sm font-medium flex items-center justify-between cursor-pointer"
        >
          <span>
            <Search className="w-4 h-4 mr-2 inline-block" />
            {buttonLabel || "Search Users..."}
          </span>
        </Label>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Search Users</DialogTitle>
          <DialogDescription>
            Search for a user by name, email, code, or phone number.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="col-span-3"
              placeholder="Enter name, email, code, or phone..."
            />
          </div>
        </div>
        <ScrollArea className="h-[300px] w-full rounded-md border">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : filteredGuards.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No users found.</div>
          ) : (
            filteredGuards.map((guard) => (
              <button
                key={guard.id}
                className="flex items-center space-x-2 p-4 hover:bg-gray-100 w-full text-left"
                onClick={() => handleGuardSelect(guard)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={guard.avatar_url || `https://avatar.vercel.sh/${guard.last_name} ${guard.first_name}.png`} />
                  <AvatarFallback className="bg-primary/50 text-primary">
                    {guard.first_name?.[0]}
                    {guard.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium leading-none">
                    {guard.last_name} {guard.first_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {guard.email || guard.code || guard.mobile_phone}
                  </p>
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
